<template>
    <ModalShell
        :title="headerTitle"
        :subtitle="headerSubtitle"
        width="w-full"
        body-class="sm:!pl-0"
        @keydown.enter="handleEnter"
    >
        <template #icon>
            <PhBaby v-if="modalState === 'form'" :size="24" />
            <PhShieldCheck v-else :size="24" />
        </template>
        <template #header-action>
            <button type="button" class="close-btn" aria-label="Close" :disabled="isSubmitting || isSendingConsent" @click="closeDialog">
                <PhX :size="20" />
            </button>
        </template>

        <!-- Loading -->
        <div v-if="modalState === 'loading'" class="loading-state">
            <PrimeProgressSpinner style="width: 32px; height: 32px;" strokeWidth="3" />
            <p class="loading-text">Checking consent status…</p>
        </div>

        <!-- Consent required -->
        <div v-else-if="modalState === 'consent_required'" class="w-full">
            <div class="info-card">
                <PhFileText :size="28" class="info-card__icon" />
                <div class="info-card__body">
                    <h3>Waiting on a parent or guardian</h3>
                    <p>
                        <strong>{{ employeeName }}</strong> hasn't signed the short consent
                        form yet, so we can't add a child to their account.
                    </p>
                    <p class="info-card__hint">
                        We'll email them a secure link to read and sign it. Once that's
                        done, you can add their child.
                    </p>
                </div>
            </div>
        </div>

        <!-- Consent email sent -->
        <div v-else-if="modalState === 'consent_sent'" class="w-full">
            <div class="info-card info-card--success">
                <PhCheckCircle :size="28" weight="fill" class="info-card__icon info-card__icon--success" />
                <div class="info-card__body">
                    <h3>Email sent</h3>
                    <p>We've emailed the consent form to <strong>{{ employeeName }}</strong>.</p>
                    <p class="info-card__hint">
                        Once they've read and signed it, you can add their child.
                    </p>
                </div>
            </div>
        </div>

        <!-- Child form -->
        <form v-else class="w-full max-h-[60vh] overflow-y-auto" @submit.prevent="submitChild">
            <div class="form-grid">
                <div class="form-row form-row--span-2">
                    <label>First Name <span class="text-red-500">*</span></label>
                    <PrimeInputText v-model.trim="form.first_name" placeholder="First name" class="w-full" />
                </div>
                <div class="form-row">
                    <label>Middle</label>
                    <PrimeInputText v-model.trim="form.middle_name" placeholder="Middle" class="w-full" />
                </div>
                <div class="form-row form-row--span-2">
                    <label>Last Name <span class="text-red-500">*</span></label>
                    <PrimeInputText v-model.trim="form.last_name" placeholder="Last name" class="w-full" />
                </div>
                <div class="form-row form-row--span-full">
                    <label>Date of Birth <span class="text-red-500">*</span></label>
                    <button type="button" class="dob-trigger" @click="showDobPicker = !showDobPicker">
                        <PhCalendarBlank :size="18" class="text-zinc-500" />
                        <span :class="{ 'text-zinc-400': !form.dob }">
                            {{ formatDate(form.dob) || 'Pick a date' }}
                        </span>
                        <button
                            v-if="form.dob"
                            type="button"
                            class="dob-clear"
                            @click.stop="form.dob = ''"
                            aria-label="Clear date"
                        >
                            <PhX :size="14" />
                        </button>
                    </button>
                    <!-- Inline picker. The previous absolute-positioned popover got
                         clipped by the modal's overflow-hidden corner, so the picker
                         now renders in flow and pushes the rest of the form down. -->
                    <div v-if="showDobPicker" class="dob-inline">
                        <CustomDatePicker
                            :initial-date="dobInitialDate"
                            @cancel="showDobPicker = false"
                            @apply="onDobApplied"
                        />
                    </div>
                    <span v-if="dobError" class="form-error">{{ dobError }}</span>
                </div>

                <div class="form-row form-row--span-full">
                    <label class="label-with-tag">
                        <span>Phone</span>
                        <span class="optional-tag">Optional</span>
                    </label>
                    <PhoneFields v-model="form.phone" />
                </div>

                <div class="form-row form-row--span-full">
                    <label class="label-with-tag">
                        <span>Address</span>
                        <span class="optional-tag">Optional</span>
                    </label>
                    <AddressFields v-model="form.address" />
                </div>
            </div>
        </form>

        <template #footer>
            <template v-if="modalState === 'loading'">
                <PrimeButton label="Cancel" severity="secondary" @click="closeDialog" />
            </template>

            <template v-else-if="modalState === 'consent_required'">
                <PrimeButton label="Cancel" severity="secondary" @click="closeDialog" :disabled="isSendingConsent" />
                <PrimeButton
                    label="Send Consent Email"
                    :loading="isSendingConsent"
                    @click="sendConsentEmail"
                >
                    <template #icon>
                        <PhEnvelope :size="16" />
                    </template>
                </PrimeButton>
            </template>

            <template v-else-if="modalState === 'consent_sent'">
                <PrimeButton label="Done" @click="closeDialog" />
            </template>

            <template v-else>
                <PrimeButton label="Cancel" severity="secondary" @click="closeDialog" :disabled="isSubmitting" />
                <PrimeButton
                    label="Add Child"
                    :loading="isSubmitting"
                    :disabled="!canSubmit"
                    @click="submitChild"
                />
            </template>
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
import {
    PhBaby,
    PhCalendarBlank,
    PhCheckCircle,
    PhEnvelope,
    PhFileText,
    PhShieldCheck,
    PhX,
} from '@phosphor-icons/vue';
import PrimeButton from 'primevue/button';
import PrimeInputText from 'primevue/inputtext';
import PrimeProgressSpinner from 'primevue/progressspinner';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import AddressFields from '~/components/shared/forms/AddressFields.vue';
import PhoneFields from '~/components/shared/forms/PhoneFields.vue';
import CustomDatePicker from '~/components/main/shared/CustomDatePicker.vue';
import { formatDate } from '@privacy-bee/format/date';
import { businessService } from '~/services/business-service';
import { enterFromField } from '~/utils/dialog-enter';
import { ModalNames } from '~/types/modals';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { optionalPhonePayload, optionalPrefixedAddressPayload } from '~/utils/employee-contact-payload';
import { ApiError, memberFacingMessage } from '~/utils/errors';

const dialogRef = getDialogRef(ModalNames.ADD_EMPLOYEE_CHILD);
const { showSuccess, showError } = useGlobalToast();

const companyId = computed(() => dialogRef.value?.data?.companyId as number | undefined);
const employeeId = computed(() => dialogRef.value?.data?.employeeId as number | undefined);
const employeeName = computed(() => dialogRef.value?.data?.employeeName as string ?? 'this employee');

// `consentSigned` from the opener is a hint only — we always re-fetch on
// open so a stale drawer view can't trick us into rendering the form when
// consent has since been revoked or never existed.
type ModalState = 'loading' | 'consent_required' | 'consent_sent' | 'form';
const modalState = ref<ModalState>('loading');

const isSendingConsent = ref(false);
const isSubmitting = ref(false);
const childCreated = ref(false);

const form = reactive({
    first_name: '',
    middle_name: '',
    last_name: '',
    dob: '' as string,
    phone: { country_code: '1', phone_number: '' },
    address: { line_1: '', line_2: '', city: '', region: '', postal_code: '', country: '' },
});

const showDobPicker = ref(false);

// Default the calendar to ~8 years ago so admins don't have to scroll
// back from today's month to find a typical kid's birth year.
const dobInitialDate = computed<Date>(() => {
    if (form.dob) return new Date(form.dob + 'T12:00:00');
    const now = new Date();
    return new Date(now.getFullYear() - 8, now.getMonth(), now.getDate(), 12, 0, 0);
});

const headerTitle = computed(() => {
    if (modalState.value === 'form') return `Add Child for ${employeeName.value}`;
    if (modalState.value === 'consent_sent') return 'Request sent';
    return 'One step first';
});

const headerSubtitle = computed(() => {
    if (modalState.value === 'form') return 'Add a minor child to this employee\'s family.';
    if (modalState.value === 'consent_sent') return 'They can sign whenever they\'re ready.';
    return 'A parent or guardian signs a short form, then you can add the child.';
});

const dobError = computed<string | null>(() => {
    if (!form.dob) return null;
    const dob = new Date(form.dob + 'T12:00:00');
    if (Number.isNaN(dob.getTime())) return 'Invalid date';
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
    if (age >= 18) return 'Child must be under 18 years old.';
    return null;
});

const canSubmit = computed(() => {
    if (isSubmitting.value) return false;
    if (form.first_name.trim().length < 2) return false;
    if (form.last_name.trim().length < 2) return false;
    if (!form.dob) return false;
    if (dobError.value) return false;
    return true;
});

function closeDialog() {
    // Lock every close path while a request is in flight: closing early makes
    // the parent drawer refresh before createEmployeeMinorChild() finishes, so
    // a successfully created child would be missing from the family list. The
    // success path still closes (childCreated flips before isSubmitting clears).
    if ((isSubmitting.value && !childCreated.value) || isSendingConsent.value) return;
    dialogRef.value?.close({ created: childCreated.value });
}

// Enter submits from a field only: on Cancel or the date trigger it must do what the button says.
function handleEnter(event: KeyboardEvent) {
    if (!enterFromField(event)) return;
    event.preventDefault();
    if (modalState.value === 'form' && canSubmit.value) submitChild();
    else if (modalState.value === 'consent_required' && !isSendingConsent.value) sendConsentEmail();
    else if (modalState.value === 'consent_sent') closeDialog();
}

function onDobApplied(value: Date) {
    // Noon-local trick (matches CreateEmployeeModal/EditDobModal) so the
    // YYYY-MM-DD string doesn't get bumped by timezone offsets.
    const local = new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12, 0, 0);
    form.dob = `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}-${String(local.getDate()).padStart(2, '0')}`;
    showDobPicker.value = false;
}

// Picker is rendered inline (not as a floating popover) so no click-outside
// handler is needed — users dismiss it via Cancel inside the picker, by
// applying a date, or by toggling the trigger again.

async function checkConsent() {
    if (!companyId.value || !employeeId.value) return;
    modalState.value = 'loading';
    try {
        const { consent_signed } = await businessService.getEmployeeMinorConsentStatus(
            companyId.value,
            employeeId.value,
        );
        modalState.value = consent_signed ? 'form' : 'consent_required';
    } catch (error) {
        // Fail closed: any failure (403/404/500/network) keeps the admin
        // out of the form, because a consent state nobody read is not a
        // signature, and the form would let them add the child without one.
        modalState.value = 'consent_required';
        showError('Could not check consent status', memberFacingMessage(error, 'Try again in a moment.'));
    }
}

async function sendConsentEmail() {
    if (!companyId.value || !employeeId.value) return;
    isSendingConsent.value = true;
    try {
        await businessService.requestEmployeeMinorConsent(companyId.value, employeeId.value);
        modalState.value = 'consent_sent';
        showSuccess('Email sent', `Consent request emailed to ${employeeName.value}.`);
    } catch (error) {
        showError('Error', memberFacingMessage(error, 'Failed to send consent email.'));
    } finally {
        isSendingConsent.value = false;
    }
}

async function submitChild() {
    if (!companyId.value || !employeeId.value || !canSubmit.value) return;
    isSubmitting.value = true;
    try {
        const payload: Parameters<typeof businessService.createEmployeeMinorChild>[2] = {
            first_name: form.first_name.trim(),
            middle_name: form.middle_name.trim() || null,
            last_name: form.last_name.trim(),
            dob: form.dob,
        };

        // Phone and address are optional; the shared rule attaches each only when
        // it is filled in enough for the backend's required-with rules.
        payload.phone_number = optionalPhonePayload(form.phone);
        payload.address = optionalPrefixedAddressPayload(form.address);

        await businessService.createEmployeeMinorChild(companyId.value, employeeId.value, payload);
        childCreated.value = true;
        showSuccess('Child added', `${form.first_name} has been added to ${employeeName.value}'s family.`);
        closeDialog();
    } catch (error) {
        // Backend defense-in-depth: if consent was revoked / never signed
        // between the open check and the submit (or the open check was
        // somehow bypassed), the POST returns 409. Recover by flipping
        // back to the consent_required state instead of leaving the user
        // staring at a generic toast.
        if (error instanceof ApiError && error.code === 409) {
            modalState.value = 'consent_required';
            showError('Still waiting on consent', memberFacingMessage(error, 'A parent or guardian needs to sign the form first.'));
        } else {
            showError('Error', memberFacingMessage(error, 'Failed to add child.'));
        }
    } finally {
        isSubmitting.value = false;
    }
}

watch([companyId, employeeId], () => {
    if (companyId.value && employeeId.value) checkConsent();
}, { immediate: true });
</script>

<style lang="scss" scoped>

.close-btn {
    @apply flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700;
}

.loading-state {
    @apply flex w-full flex-col items-center justify-center gap-3 py-6;
}

.form-grid {
    @apply grid grid-cols-5 gap-3 pt-2;
}

.form-row {
    @apply col-span-1 flex flex-col;

    &--span-2 {
        @apply col-span-2;
    }

    &--span-full {
        @apply col-span-5 relative;
    }

    label {
        @apply block text-sm font-medium text-zinc-700 mb-1.5;
    }
}

.form-error {
    @apply text-xs text-red-600 mt-1;
}

.dob-trigger {
    @apply w-full flex items-center gap-2 px-3 py-2 border border-zinc-300 rounded-md bg-white text-sm text-zinc-800 cursor-pointer transition-colors;

    &:hover {
        @apply border-zinc-400;
    }

    .dob-clear {
        @apply ml-auto p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100;
    }
}

// Renders the date picker in flow under the DOB trigger. A subtle border keeps the
// boundary between picker and the rest of the form visible once it expands.
.dob-inline {
    @apply mt-2 rounded-lg border border-zinc-200 bg-white overflow-hidden;
    box-shadow: var(--shadow-overlay);
}

.label-with-tag {
    @apply flex items-center justify-between gap-2 #{!important};

    .optional-tag {
        @apply text-xs font-normal text-zinc-400;
    }
}

.info-card {
    @apply flex items-start gap-4 p-4 mt-2 rounded-lg border border-zinc-200 bg-zinc-50;

    &--success {
        @apply border-green-200 bg-green-50;
    }

    &__icon {
        @apply text-zinc-500 flex-shrink-0 mt-0.5;

        &--success {
            @apply text-green-600;
        }
    }

    &__body {
        @apply flex-1 min-w-0;

        h3 {
            @apply text-sm font-semibold text-zinc-900 m-0 mb-1;
        }

        p {
            @apply text-sm text-zinc-600 m-0 mb-2;

            &:last-child {
                @apply mb-0;
            }
        }
    }

    &__hint {
        @apply text-xs text-zinc-500 #{!important};
    }
}

.loading-text {
    @apply text-sm text-zinc-500 m-0;
}
</style>
